import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Plus, Star, Check, Truck, Leaf, ShieldCheck, ArrowUpRight, Loader2 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { PRODUCTS } from "../mock";
import { PRODUCT_DETAILS, TX } from "../data";
import { getProducts } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const normalizeDynamic = (d) => ({
  id: d.product_id,
  index: "",
  tag: d.tag || "NEW",
  preorder: !!d.preorder,
  image: d.image,
  price: d.price,
  volume: "",
  category: { nl: d.category || "", en: d.category || "" },
  name: { nl: d.name, en: d.name },
  subtitle: { nl: d.subtitle || "", en: d.subtitle || "" },
  short: { nl: d.description || "", en: d.description || "" },
});

const ProductDetail = () => {
  const { id } = useParams();
  const { t, lang, addToCart, openWaitlist } = useApp();
  const tx = TX[lang];
  const navigate = useNavigate();
  const curated = PRODUCTS.find((p) => p.id === id);
  const [dynamic, setDynamic] = useState(null);
  const [loading, setLoading] = useState(!curated);
  const product = curated || dynamic;
  const details = PRODUCT_DETAILS[id];
  const hasShades = Array.isArray(product?.shades) && product.shades.length > 0;
  const [shade, setShade] = useState(hasShades ? product.shades[0] : null);

  useEffect(() => {
    let active = true;
    if (!curated) {
      setLoading(true);
      getProducts()
        .then((res) => {
          const found = (res.dynamic || []).find((d) => d.product_id === id);
          if (active) setDynamic(found ? normalizeDynamic(found) : null);
        })
        .catch(() => active && setDynamic(null))
        .finally(() => active && setLoading(false));
    }
    return () => { active = false; };
  }, [id, curated]);

  if (loading) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center text-[#B4715C]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </main>
    );
  }

  if (!product) {
    return (
      <main className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <p className="font-serif text-3xl text-[#3A2C28]">404</p>
        <Link to="/" className="text-[12px] tracking-label uppercase text-[#B4715C] link-underline">{tx.backToShop}</Link>
      </main>
    );
  }

  const onAction = () => {
    if (product.preorder) openWaitlist(product.id);
    else addToCart(product, shade, 1);
  };

  return (
    <main className="max-w-[1320px] mx-auto px-6 py-12 md:py-16">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-[12px] tracking-label uppercase text-[#B4715C] hover:text-[#9E5F4B]">
        <ArrowLeft className="h-4 w-4" /> {tx.backToShop}
      </button>

      <div className="mt-8 grid md:grid-cols-2 gap-12 items-start">
        {/* Image */}
        <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-[#F3E0D6] to-[#E7C3B4] border border-[#EEE0D7] md:sticky md:top-24">
          <div className="absolute inset-0 opacity-80" style={{ background: "radial-gradient(55% 50% at 50% 42%, rgba(212,150,128,0.5), transparent 70%)" }} />
          {product.preorder && (
            <span className="absolute top-5 right-5 z-10 text-[10px] tracking-label uppercase px-3 py-1 rounded-full bg-[#3A2C28] text-[#F4E6DE]">{t.preorder}</span>
          )}
          <img src={product.image} alt={product.name[lang]} className="relative z-[1] w-full h-[460px] md:h-[560px] object-contain p-12" style={{ filter: "drop-shadow(0 26px 30px rgba(120,80,60,0.28))" }} />
        </div>

        {/* Info */}
        <div>
          <p className="text-[11px] tracking-label uppercase text-[#B4715C]">{product.category[lang]}</p>
          <h1 className="mt-3 font-serif font-light text-5xl md:text-6xl text-[#3A2C28] leading-[1.02]">{product.name[lang]}</h1>
          <p className="mt-2 text-[12px] tracking-label uppercase text-[#A08678]">{product.subtitle[lang]}</p>

          <div className="mt-4 flex items-center gap-2">
            {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-[#C79A7E] text-[#C79A7E]" />)}
            <span className="text-[12px] text-[#8A756C] ml-1">{details?.reviews.length} {tx.reviews.toLowerCase()}</span>
          </div>

          <p className="mt-6 text-[15px] leading-[1.8] text-[#6E5A52]">{details?.longDesc[lang] || product.short[lang]}</p>

          {hasShades && (
            <div className="mt-7">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[11px] tracking-label uppercase text-[#8A756C]">{t.chooseShade}</p>
                <span className="text-[12px] text-[#B4715C]">{shade?.code} · {shade?.name}</span>
              </div>
              <div className="flex items-center gap-4">
                {product.shades.map((s) => (
                  <button key={s.code} onClick={() => setShade(s)} aria-label={`Shade ${s.code}`}
                    className={`h-10 w-10 rounded-full transition-all ${shade?.code === s.code ? "ring-2 ring-offset-2 ring-[#B4715C] scale-110" : "ring-1 ring-[#E8D6CB] hover:scale-105"}`}
                    style={{ backgroundColor: s.hex, boxShadow: "inset 0 -3px 6px rgba(0,0,0,0.15), inset 0 3px 5px rgba(255,255,255,0.5)" }} />
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-end gap-5">
            <div>
              <span className="block text-[10px] tracking-label uppercase text-[#A08678] mb-0.5">{product.volume}</span>
              <span className="font-serif text-4xl text-[#3A2C28] leading-none">€{product.price.toFixed(2)}</span>
            </div>
            <button onClick={onAction}
              className="group/btn flex-1 md:flex-none flex items-center justify-center gap-2 h-14 px-10 rounded-full bg-[#3A2C28] text-white text-[11px] tracking-label uppercase hover:bg-[#B4715C]">
              {product.preorder ? tx.preorderCta : tx.addToCart}
              {product.preorder ? <ArrowUpRight className="h-4 w-4" /> : <Plus className="h-4 w-4 transition-transform group-hover/btn:rotate-90" />}
            </button>
          </div>

          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[12px] text-[#8A756C]">
            <span className="inline-flex items-center gap-1.5"><Truck className="h-4 w-4 text-[#B4715C]" /> {tx.shippingLine}</span>
            <span className="inline-flex items-center gap-1.5"><Leaf className="h-4 w-4 text-[#B4715C]" /> Vegan</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-[#B4715C]" /> Dermatologisch getest</span>
          </div>

          {/* Tabs */}
          {details && (
          <div className="mt-10">
            <Tabs defaultValue="ingredients">
              <TabsList className="bg-[#F3E7DF] rounded-full p-1">
                <TabsTrigger value="ingredients" className="rounded-full text-[11px] tracking-label uppercase data-[state=active]:bg-white">{tx.ingredients}</TabsTrigger>
                <TabsTrigger value="howto" className="rounded-full text-[11px] tracking-label uppercase data-[state=active]:bg-white">{tx.howToUse}</TabsTrigger>
                <TabsTrigger value="reviews" className="rounded-full text-[11px] tracking-label uppercase data-[state=active]:bg-white">{tx.reviews}</TabsTrigger>
              </TabsList>

              <TabsContent value="ingredients" className="mt-6">
                <p className="text-[11px] tracking-label uppercase text-[#A08678] mb-4">{tx.keyIngredients}</p>
                <ul className="space-y-4">
                  {details?.ingredients.map((ing) => (
                    <li key={ing.name} className="flex gap-3">
                      <span className="h-6 w-6 shrink-0 rounded-full bg-[#F4E6DE] flex items-center justify-center text-[#B4715C]"><Check className="h-3.5 w-3.5" /></span>
                      <div>
                        <p className="text-[15px] text-[#3A2C28] font-medium">{ing.name}</p>
                        <p className="text-sm text-[#6E5A52]">{ing[lang]}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </TabsContent>

              <TabsContent value="howto" className="mt-6">
                <ol className="space-y-4">
                  {details?.howToUse.map((step, i) => (
                    <li key={i} className="flex gap-4">
                      <span className="font-serif text-2xl text-[#C79A7E] leading-none w-7">{i + 1}</span>
                      <p className="text-[15px] text-[#6E5A52] pt-0.5">{step[lang]}</p>
                    </li>
                  ))}
                </ol>
              </TabsContent>

              <TabsContent value="reviews" className="mt-6">
                <div className="space-y-5">
                  {details?.reviews.map((r, i) => (
                    <div key={i} className="bg-white rounded-2xl border border-[#EEE0D7] p-6">
                      <div className="flex gap-1">{[...Array(r.rating)].map((_, j) => <Star key={j} className="h-4 w-4 fill-[#C79A7E] text-[#C79A7E]" />)}</div>
                      <p className="mt-3 font-serif text-lg text-[#3A2C28] italic leading-snug">“{r[lang]}”</p>
                      <p className="mt-3 text-[11px] tracking-label uppercase text-[#8A756C]">{r.name}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default ProductDetail;
